import React, { useMemo } from 'react';
import moment from 'moment-timezone';
import { useSelector } from 'react-redux';

import { parse } from '../../util/urlHelpers';
import { LISTING_TYPE_GROUP_COACHING } from '../../util/types';
import { makeGetListingsByIdSelector } from '../../ducks/marketplaceData.duck';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import SearchPageAccessWrapper from './SearchPageAccessWrapper';
import SearchPageShell from './SearchPageShell';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import {
  filterListingsByLanguage,
  filterListingsBySessionLength,
  getLanguageEnumOptions,
  getLanguageIdsFromParam,
  isListingLanguageIndexed,
  LANGUAGE_QUERY_PARAM,
  SESSION_LENGTH_QUERY_PARAM,
} from './SearchFiltersBar/searchFiltersBar.helpers';

import css from './SearchPage.module.css';

export const isGroupCoachingListingActive = listing => {
  const { publicData: { listingType, sessionDates } = {}, availabilityPlan } =
    listing.attributes || {};

  if (listingType !== LISTING_TYPE_GROUP_COACHING) return true;
  if (!sessionDates || sessionDates.length === 0) return false;

  const timezone = availabilityPlan?.timezone || 'Asia/Calcutta';
  const now = moment().tz(timezone);

  const earliestSession = sessionDates.reduce((min, s) => {
    const dt = moment.tz(`${s.date} ${s.startTime || '00:00'}`, 'YYYY-MM-DD HH:mm', timezone);
    const minDt = moment.tz(
      `${min.date} ${min.startTime || '00:00'}`,
      'YYYY-MM-DD HH:mm',
      timezone
    );
    return dt.isBefore(minDt) ? s : min;
  });

  const earliestDateTime = moment.tz(
    `${earliestSession.date} ${earliestSession.startTime || '00:00'}`,
    'YYYY-MM-DD HH:mm',
    timezone
  );

  return earliestDateTime.isAfter(now);
};

const SearchPageForCoursesComponent = props => {
  const {
    intl,
    listings = [],
    featuredListings = [],
    location,
    pagination,
    scrollingDisabled,
    searchInProgress,
    searchListingsError,
    searchParams,
    noResultsForKeywords,
    routeConfiguration,
    config,
    currentUser,
    params: currentPathParams = {},
  } = props;

  const urlParams = parse(location.search);
  const activeListings = listings.filter(isGroupCoachingListingActive);
  // Flagship carousel: the separately fetched `meta_featured` courses, with expired
  // group programmes hidden the same way as in the results below.
  const activeFeaturedListings = featuredListings.filter(isGroupCoachingListingActive);
  const languageIndexed = isListingLanguageIndexed(config);
  const languageFiltered = languageIndexed
    ? activeListings
    : filterListingsByLanguage(activeListings, urlParams, getLanguageEnumOptions(config));
  const filteredListings = filterListingsBySessionLength(languageFiltered, urlParams);
  const clientLanguageActive =
    !languageIndexed && getLanguageIdsFromParam(urlParams[LANGUAGE_QUERY_PARAM]).length > 0;
  const clientSessionActive =
    getLanguageIdsFromParam(urlParams[SESSION_LENGTH_QUERY_PARAM]).length > 0;
  const resultsCount =
    clientLanguageActive || clientSessionActive
      ? filteredListings.length
      : pagination?.totalItems ?? filteredListings.length;

  return (
    <SearchPageShell
      intl={intl}
      location={location}
      routeConfiguration={routeConfiguration}
      config={config}
      currentUser={currentUser}
      scrollingDisabled={scrollingDisabled}
      searchInProgress={searchInProgress}
      searchListingsError={searchListingsError}
      searchParams={searchParams}
      noResultsForKeywords={noResultsForKeywords}
      pagination={pagination}
      listings={filteredListings}
      featuredListings={activeFeaturedListings}
      currentPathParams={currentPathParams}
      featuredVariant="course"
      titleMessageId="SearchPageForCourses.title"
      subtitleMessageId="SearchPageForCourses.subtitle"
      resultsCount={resultsCount}
    >
      <SearchResultsPanel
        className={css.searchListingsPanel}
        listings={filteredListings}
        pagination={pagination}
        search={urlParams}
        listingTypeParam={currentPathParams.listingType}
        intl={intl}
      />
    </SearchPageShell>
  );
};

/**
 * Course listing search page.
 *
 * @param {Object} props
 * @returns {JSX.Element}
 */
const SearchPageForCourses = props => {
  const selectListingsById = useMemo(makeGetListingsByIdSelector, []);
  // Separate instance: a selector caches one input, so sharing it between the two
  // id lists would recompute (and re-render) on every store update.
  const selectFeaturedListingsById = useMemo(makeGetListingsByIdSelector, []);
  const currentUser = useSelector(state => state.user?.currentUser);
  const {
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
    noResultsForKeywords,
  } = useSelector(state => state.SearchPage);
  const listings = useSelector(state =>
    selectListingsById(state, state.SearchPage.currentPageResultIds)
  );
  const featuredListings = useSelector(state =>
    selectFeaturedListingsById(state, state.SearchPage.featuredResultIds)
  );
  const scrollingDisabled = useSelector(state => isScrollingDisabled(state));

  return (
    <SearchPageAccessWrapper
      {...props}
      PageComponent={SearchPageForCoursesComponent}
      currentUser={currentUser}
      listings={listings}
      featuredListings={featuredListings}
      pagination={pagination}
      scrollingDisabled={scrollingDisabled}
      searchInProgress={searchInProgress}
      searchListingsError={searchListingsError}
      searchParams={searchParams}
      noResultsForKeywords={noResultsForKeywords}
    />
  );
};

export default SearchPageForCourses;
